// Tiny pub/sub state container. Persists to localStorage, hydrates from URL.

import { MAIN_STAGES, STAGE_TAG, TAG_TO_STAGE } from "../data/stages";
import { DEVICES, DEFAULT_DEVICE } from "../data/devices";

export type AppState = {
  day: 1 | 2 | 3;
  stages: string[];          // selected stages (canonical names)
  faves: Set<string>;        // favorited performance IDs
  deviceId: string;
  spotlightFaves: boolean;
  search: string;
};

const LS_KEY = "edc2026.state.v1";

const DEFAULTS: AppState = {
  day: 1,
  stages: [...MAIN_STAGES],
  faves: new Set(),
  deviceId: DEFAULT_DEVICE.id,
  spotlightFaves: false,
  search: "",
};

type Listener = (s: AppState) => void;

class Store {
  private state: AppState;
  private listeners: Set<Listener> = new Set();

  constructor() {
    this.state = { ...DEFAULTS, ...this.fromURL(), ...this.fromLocalStorage() };
    // URL takes precedence over localStorage:
    const fromURL = this.fromURL();
    if (fromURL) {
      this.state = { ...DEFAULTS, ...this.fromLocalStorage(), ...fromURL };
    }
  }

  get(): Readonly<AppState> {
    return this.state;
  }

  update(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.persist();
    this.listeners.forEach(l => l(this.state));
  }

  toggleFave(id: string): void {
    const next = new Set(this.state.faves);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.update({ faves: next });
  }

  toggleStage(stage: string): void {
    const has = this.state.stages.includes(stage);
    const next = has
      ? this.state.stages.filter(s => s !== stage)
      : [...this.state.stages, stage].sort((a, b) => MAIN_STAGES.indexOf(a as any) - MAIN_STAGES.indexOf(b as any));
    this.update({ stages: next });
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private persist(): void {
    try {
      const serial = {
        day: this.state.day,
        stages: this.state.stages,
        faves: [...this.state.faves],
        deviceId: this.state.deviceId,
        spotlightFaves: this.state.spotlightFaves,
      };
      localStorage.setItem(LS_KEY, JSON.stringify(serial));
    } catch { /* localStorage may be unavailable */ }
  }

  private fromLocalStorage(): Partial<AppState> {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return {};
      const j = JSON.parse(raw);
      return {
        day: j.day,
        stages: Array.isArray(j.stages) ? j.stages : undefined,
        faves: Array.isArray(j.faves) ? new Set(j.faves) : undefined,
        deviceId: j.deviceId,
        spotlightFaves: !!j.spotlightFaves,
      };
    } catch {
      return {};
    }
  }

  private fromURL(): Partial<AppState> | null {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    const out: Partial<AppState> = {};
    const day = parseInt(p.get("day") ?? "");
    if (day === 1 || day === 2 || day === 3) out.day = day;
    const stages = p.get("stages");
    if (stages) {
      out.stages = stages.split(",").map(t => TAG_TO_STAGE[t.toUpperCase()]).filter(Boolean);
    }
    const faves = p.get("faves");
    if (faves) out.faves = new Set(faves.split(",").filter(Boolean));
    const dev = p.get("device");
    if (dev && DEVICES.find(d => d.id === dev)) out.deviceId = dev;
    if (p.get("spotlight") === "1") out.spotlightFaves = true;
    return Object.keys(out).length ? out : null;
  }

  toURL(): string {
    const p = new URLSearchParams();
    p.set("day", String(this.state.day));
    p.set("stages", this.state.stages.map(s => STAGE_TAG[s] ?? "").filter(Boolean).join(","));
    if (this.state.faves.size > 0) {
      p.set("faves", [...this.state.faves].join(","));
    }
    p.set("device", this.state.deviceId);
    if (this.state.spotlightFaves) p.set("spotlight", "1");
    const base = window.location.origin + window.location.pathname;
    return `${base}?${p.toString()}`;
  }
}

export const store = new Store();
